import { useState, useCallback, useRef, useEffect } from 'react';
import { workoutAPI } from '../services/workoutAPI';

const DEBOUNCE_MS = 3000; // 3 seconds - faster saves while preventing spam

export const useWorkoutDraft = () => {
  const [hasDraft, setHasDraft] = useState(false);
  const [draftAge, setDraftAge] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const saveTimeoutRef = useRef(null);
  const lastDraftDataRef = useRef(null);

  // Check if draft exists (call this on mount)
  const checkDraft = useCallback(async () => {
    try {
      const draft = await workoutAPI.loadDraft();

      if (draft && draft.updated_at) {
        setHasDraft(true);

        // Calculate age
        const now = Date.now();
        const draftTime = new Date(draft.updated_at).getTime();
        const ageMs = now - draftTime;

        const hours = Math.floor(ageMs / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (hours < 1) {
          setDraftAge('less than an hour ago');
        } else if (hours < 24) {
          setDraftAge(`${hours} hour${hours > 1 ? 's' : ''} ago`);
        } else {
          setDraftAge(`${days} day${days > 1 ? 's' : ''} ago`);
        }
      } else {
        setHasDraft(false);
        setDraftAge(null);
      }

      return draft;
    } catch (err) {
      console.error('Error checking draft:', err);
      setHasDraft(false);
      return null;
    }
  }, []);

  // Load draft with full data
  const loadDraft = useCallback(async () => {
    setIsLoading(true);
    try {
      const draft = await workoutAPI.loadDraft();
      console.log('📥 Draft loaded from server:', draft);
      return draft;
    } catch (err) {
      console.error('Error loading draft:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save draft (debounced to 10 seconds)
  const saveDraft = useCallback((workoutState) => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Store for reference
    lastDraftDataRef.current = workoutState;

    // Debounce the save
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const draftData = {
          workoutName: workoutState.workoutName,
          workoutDate: workoutState.workoutDate,
          workoutTime: workoutState.workoutTime,
          workoutNotes: workoutState.workoutNotes,
          durationMinutes: workoutState.durationMinutes,
          elapsedSeconds: workoutState.elapsedSeconds,
          workoutExercises: workoutState.workoutExercises
        };

        await workoutAPI.saveDraft(draftData);
        console.log('💾 Draft saved to server');
        setHasDraft(true);
      } catch (err) {
        console.error('Error saving draft:', err);
        // Don't show error to user - just log it
        // Draft will retry on next change
      }
    }, DEBOUNCE_MS);
  }, []);

  // Clear draft
  const clearDraft = useCallback(async () => {
    // Clear pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    try {
      await workoutAPI.clearDraft();
      console.log('🗑️ Draft cleared from server');
      setHasDraft(false);
      setDraftAge(null);
    } catch (err) {
      console.error('Error clearing draft:', err);
    }
  }, []);

  // Cleanup on unmount - save pending draft immediately
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current && lastDraftDataRef.current) {
        clearTimeout(saveTimeoutRef.current);

        // Immediately save pending draft before unmounting
        const draftData = {
          workoutName: lastDraftDataRef.current.workoutName,
          workoutDate: lastDraftDataRef.current.workoutDate,
          workoutTime: lastDraftDataRef.current.workoutTime,
          workoutNotes: lastDraftDataRef.current.workoutNotes,
          durationMinutes: lastDraftDataRef.current.durationMinutes,
          elapsedSeconds: lastDraftDataRef.current.elapsedSeconds,
          workoutExercises: lastDraftDataRef.current.workoutExercises
        };

        workoutAPI.saveDraft(draftData)
          .then(() => console.log('💾 Draft saved on unmount'))
          .catch(err => console.error('Error saving draft on unmount:', err));
      }
    };
  }, []);

  return {
    hasDraft,
    draftAge,
    isLoading,
    checkDraft,
    loadDraft,
    saveDraft,
    clearDraft,
  };
};
